import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FiX, FiChevronDown } from 'react-icons/fi'
import { COUNTRIES } from '@/constants/countries'
import { isApprovedSupplierName } from '@/constants/suppliers'
import { createVendor } from '@/services/api/vendors'
import type { Vendor } from '@/services/api/vendors'

interface AddSupplierModalProps {
  isOpen: boolean
  onClose: () => void
  onSupplierAdded?: (vendor: Vendor) => void
}

const AddSupplierModal = ({ isOpen, onClose, onSupplierAdded }: AddSupplierModalProps) => {
  const [formData, setFormData] = useState({
    supplierName: '',
    emailAddress: '',
    supplierId: '',
    companyName: '',
    country: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.overflow = ''
        window.scrollTo(0, scrollY)
      }
    }
  }, [isOpen])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.supplierName.trim()) {
      setError('Supplier name is required.')
      return
    }
    if (!isApprovedSupplierName(formData.supplierName)) {
      setError('Only approved suppliers can be added: Criadero del Carmen or GBC Portugal.')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const vendor = await createVendor({
        name: formData.supplierName.trim(),
        contactPerson: formData.companyName.trim() || undefined,
        email: formData.emailAddress.trim() || undefined,
        address: formData.country.trim() || undefined,
      })
      onSupplierAdded?.(vendor)
      setFormData({
        supplierName: '',
        emailAddress: '',
        supplierId: '',
        companyName: '',
        country: ''
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add supplier')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      style={{ zIndex: 80, overflow: 'hidden' }}
    >
      <div 
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '600px',
          maxWidth: '600px',
          height: '100vh',
          zIndex: 90,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <style>{`
          #add-supplier-form input::placeholder,
          #add-supplier-form select option[value=""] {
            color: #69697A !important;
            font-family: 'Lexend Deca';
            font-weight: 300;
            font-style: 'normal';
            font-size: 12px;
            line-height: 20px;
            letter-spacing: 0px;
          }
          #add-supplier-form input:not(:placeholder-shown),
          #add-supplier-form select:not([value=""]) {
            color: #212126 !important;
            font-family: 'Lexend Deca';
            font-weight: 500;
            font-style: 'normal';
            font-size: 12px;
            line-height: 20px;
          }
        `}</style>
        
        {/* Header */}
        <div 
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderTopLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <h2 
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '26px',
              letterSpacing: '0px',
              color: '#212126'
            }}
          >
            Add Supplier
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ 
              width: '24px',
              height: '24px',
              cursor: 'pointer'
            }}
          >
            <FiX style={{ width: '24px', height: '24px', color: '#212126' }} />
          </button>
        </div>

        {/* Form Content */}
        <form 
          id="add-supplier-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          style={{
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          {error && (
            <div style={{ color: '#dc2626', fontSize: '14px', marginBottom: '4px' }}>{error}</div>
          )}
          {/* Two Column Layout */}
          <div className="grid grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="flex flex-col gap-4">
              {/* Supplier Name */}
              <div>
                <label 
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0px',
                    color: '#69697A',
                    display: 'block',
                    marginBottom: '8px'
                  }}
                >
                  Supplier Name
                </label>
                <input
                  type="text"
                  name="supplierName"
                  value={formData.supplierName}
                  onChange={handleChange}
                  placeholder="Enter customer name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontSize: '12px',
                    height: '40px',
                    border: '1px solid #E9E9EA',
                    borderRadius: '8px'
                  }}
                />
              </div>

              {/* Supplier ID */}
              <div>
                <label 
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0px',
                    color: '#69697A',
                    display: 'block',
                    marginBottom: '8px'
                  }}
                >
                  Supplier ID
                </label>
                <input
                  type="text"
                  name="supplierId"
                  value={formData.supplierId}
                  onChange={handleChange}
                  placeholder="Enter customer id"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontSize: '12px',
                    height: '40px',
                    border: '1px solid #E9E9EA',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="flex flex-col gap-4">
              {/* Email Address */}
              <div>
                <label 
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0px',
                    color: '#69697A',
                    display: 'block',
                    marginBottom: '8px'
                  }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  name="emailAddress"
                  value={formData.emailAddress}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontSize: '12px',
                    height: '40px',
                    border: '1px solid #E9E9EA',
                    borderRadius: '8px'
                  }}
                />
              </div>

              {/* Company Name */}
              <div>
                <label 
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0px',
                    color: '#69697A',
                    display: 'block',
                    marginBottom: '8px'
                  }}
                >
                  Company Name
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontSize: '12px',
                    height: '40px',
                    border: '1px solid #E9E9EA',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Country - Full Width */}
          <div>
            <label 
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 500,
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '22px',
                letterSpacing: '0px',
                color: '#69697A',
                display: 'block',
                marginBottom: '8px'
              }}
            >
              Country
            </label>
            <div className="relative">
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontSize: '12px',
                  height: '40px',
                  border: '1px solid #E9E9EA',
                  borderRadius: '8px',
                  color: formData.country ? '#212126' : '#69697A',
                  fontWeight: formData.country ? 500 : 300
                }}
              >
                <option value="" style={{ 
                  color: '#69697A', 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontSize: '12px'
                }}>Select country</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <FiChevronDown 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                style={{
                  width: '16px',
                  height: '16px',
                  opacity: 0.8,
                  color: '#212126'
                }}
              />
            </div>
          </div>
        </form>

        {/* Bottom Action Bar */}
        <div 
          className="flex items-center justify-end gap-4 flex-shrink-0 border-t border-gray-200"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderBottomLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#212126',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E9E9EA',
              borderRadius: '8px'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-supplier-form"
            disabled={submitting}
            className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#FFFFFF',
              backgroundColor: '#212126',
              borderRadius: '8px'
            }}
          >
            {submitting ? 'Adding...' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default AddSupplierModal
